<template>
  <DxHtmlEditor
    :value="value"
    :read-only="readOnly"
    :height="height"
    :placeholder="placeholder"
    @value-changed="onValueChanged"
    @focus-in="onFocusIn"
    ref="editor"
    :element-attr="{ class: 'gc-html-editor' }"
  >
    <DxToolbar v-if="!readOnly" :items="toolbarItems" />
    <!-- Lets a consumer nest a <DxValidator> the same way it would on a
         native DevExtreme editor (e.g. DxTextBox) -->
    <slot></slot>
  </DxHtmlEditor>
</template>

<script>
import { DxHtmlEditor, DxToolbar } from "devextreme-vue/html-editor";

// GCORPcx's first use of devextreme-vue/html-editor (technical-design.md §6,
// Graycorp - 2079 - Manage Email). Minimal toolbar button set — Bold/Italic/
// Underline/bulleted+numbered list/clear-formatting — per the /prototype
// session's placeholder representation (notes.md, "No precedent cited").
export default {
  name: "GcHtmlEditor",
  components: {
    DxHtmlEditor,
    DxToolbar,
  },
  props: {
    value: {
      type: String,
      default: "",
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    height: {
      type: String,
      default: "220px",
    },
    placeholder: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      toolbarItems: [
        "undo",
        "redo",
        "separator",
        {
          name: "size",
          acceptedValues: [
            "8pt",
            "10pt",
            "12pt",
            "14pt",
            "18pt",
            "24pt",
            "36pt",
          ],
        },
        {
          name: "font",
          acceptedValues: [
            "Arial",
            "Courier New",
            "Georgia",
            "Impact",
            "Lucida Console",
            "Tahoma",
            "Times New Roman",
            "Verdana",
          ],
        },
        "separator",
        "bold",
        "italic",
        "strike",
        "underline",
        "separator",
        "alignLeft",
        "alignCenter",
        "alignRight",
        "alignJustify",
        "separator",
        "orderedList",
        "bulletList",
        "separator",
        {
          name: "header",
          acceptedValues: ["1", "2", "3", "4", "5"],
        },
        "separator",
        "color",
        "background",
        "separator",
        "link",
        // "image",
        "separator",
        "clear",
      ],
    };
  },
  methods: {
    onValueChanged(e) {
      this.$emit("value-changed", e);
    },
    onFocusIn(e) {
      this.$emit("focus-in", e);
    },
    /**
     * Inserts text at the editor's current (or last known) cursor position —
     * used by the placeholder chip shelf's click-to-insert behaviour.
     * getSelection(true) both focuses the editor and returns its last-known
     * selection, per DevExtreme's documented dxHtmlEditor API.
     */
    insertTextAtSelection(text) {
      const instance = this.$refs.editor && this.$refs.editor.instance;
      if (!instance) return;

      const selection = instance.getSelection(true) || {
        index: instance.getLength(),
        length: 0,
      };

      instance.insertText(selection.index, text);
      instance.setSelection(selection.index + text.length, 0);

      // insertText/setSelection mutate the widget directly, bypassing the
      // value prop's one-way binding — re-emit so the parent's v-model-style
      // handler (and anything derived from it, e.g. the live preview) picks
      // up the inserted token.
      this.$emit("value-changed", { value: instance.option("value") });
    },
  },
};
</script>

<style lang="scss">
.gc-html-editor {
  .dx-invalid-message .dx-invalid-message-content {
    transform: translate(0px, 265px) !important;
  }

  // Defends against ancestor CSS that clamps generic `.dx-toolbar` height
  // (e.g. gc-popup's own `.dx-toolbar { height: 40px }` override, meant for
  // single-row popup action toolbars) — this toolbar wraps onto multiple
  // rows by design, so an inherited fixed height clips it and the wrapped
  // row spills over the editor content below instead of staying contained.
  .dx-htmleditor-toolbar-wrapper .dx-toolbar {
    height: auto !important;

    .dx-toolbar-items-container {
      height: auto !important;
    }
  }
}
</style>
